import { useEffect, useRef, useState } from 'react';
import { LOCATION_BEST_FIX_WINDOW_SECONDS } from '@siva/shared';

export type GeoFix = { lat: number; lng: number; accuracy_m: number; at: number };
export type GeoStatus =
  'unsupported' | 'prompt' | 'locating' | 'granted' | 'denied' | 'unavailable';
export type GeoState = {
  status: GeoStatus;
  /** Best (most accurate) fix seen in the last 30 s; older fixes are dropped as they age out. */
  fix: GeoFix | null;
  error: string | null;
  /** When watching began; the Punch screen gives the phone 30 s from here to settle. */
  startedAt: number | null;
};

/**
 * Continuous high-accuracy position while `active`. watchPosition with maximumAge 0 so every
 * reading is fresh (iPhones often start at ±5 km and improve over the next seconds), keeping the
 * most accurate reading of the last 30 s rather than the latest one. `denied` is sticky until
 * the user changes site settings; transient errors keep the last fix so nothing flickers.
 */
export function useGeolocation(active: boolean): GeoState {
  const [state, setState] = useState<GeoState>(() =>
    typeof navigator !== 'undefined' && 'geolocation' in navigator
      ? { status: 'prompt', fix: null, error: null, startedAt: null }
      : { status: 'unsupported', fix: null, error: null, startedAt: null },
  );
  const watchId = useRef<number | null>(null);
  const recent = useRef<GeoFix[]>([]);

  useEffect(() => {
    if (!active || state.status === 'unsupported' || !('geolocation' in navigator)) return;

    // Reflect an already-decided permission without prompting again.
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((p) => {
        if (p.state === 'denied') setState((s) => ({ ...s, status: 'denied' }));
        p.onchange = () => {
          if (p.state === 'denied') setState((s) => ({ ...s, status: 'denied', fix: null }));
        };
      })
      .catch(() => {});

    const startedAt = Date.now();
    recent.current = [];
    setState((s) => ({ ...s, status: s.status === 'prompt' ? 'locating' : s.status, startedAt }));

    const bestRecent = (now: number): GeoFix | null => {
      const cutoff = now - LOCATION_BEST_FIX_WINDOW_SECONDS * 1000;
      recent.current = recent.current.filter((f) => f.at >= cutoff);
      let best: GeoFix | null = null;
      // Chronological scan with <=: an older reading only wins when it is strictly more accurate,
      // so a phone that moves is not pinned to a stale point of equal accuracy.
      for (const f of recent.current) if (!best || f.accuracy_m <= best.accuracy_m) best = f;
      return best;
    };

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        recent.current.push({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          at: now,
        });
        setState((s) => ({ ...s, status: 'granted', fix: bestRecent(now), error: null }));
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          recent.current = [];
          setState((s) => ({ ...s, status: 'denied', fix: null, error: err.message }));
        } else {
          // POSITION_UNAVAILABLE or TIMEOUT: keep whatever is still recent, report the problem.
          setState((s) => {
            const fix = bestRecent(Date.now());
            return { ...s, status: fix ? 'granted' : 'unavailable', fix, error: err.message };
          });
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20_000 },
    );
    return () => {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    };
  }, [active]);

  return state;
}
